import classNames from "classnames";
import { format, isBefore, parseISO, subMonths } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-grid-system";
import warningIcon from "../../assets/warning-icon.svg";
import {
  DateString,
  GetAccountResponse,
  GetCustomerInfoRequest,
  GetPermissionToDiscussRequest,
  PermissionToDiscussPermission,
  ValidationErrors,
} from "../../lib/api/models";
import {
  useAddPermissionToDiscuss,
  useEditPermissionToDiscuss,
  useGetPermissionToDiscuss,
  useRemovePermissionToDicuss,
  useVerifyPermissionToDiscuss,
} from "../../lib/api/queries";
import { convertDateStringToMonthDayYearFormat } from "../../lib/formatting";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import StyledError from "../common/StyledError";
import Container from "../common/Container";
import DateInput from "../common/inputs/DateInput";
import Spinner from "../common/Spinner";
import StyledCheckbox from "../common/inputs/StyledCheckbox";
import StyledButton from "../common/buttons/StyledButton";
import BaseModal from "../common/modals/BaseModal/BaseModal";
import ModalCloseButton from "../common/modals/ModalCloseButton/ModalCloseButton";
import AddPermissionToDiscuss from "./AddPermissionToDiscuss";
import { AddPermisionToDiscuss } from "./AddPermissionToDiscuss/AddPermissionToDiscuss.types";
import EditPermissionToDiscuss from "./EditPermissionToDiscuss";
import { P2DEdit } from "./EditPermissionToDiscuss/EditPermissionToDiscuss.types";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./PermissionToDiscuss.module.css";
import PermissionToDiscussTable from "./PermissionToDiscussTable";
import { ChannelMapToFull } from "../../lib/channelUtil";
import { useChannelState } from "../../lib/channelContext";

const strings = {
  includeInactive: "Include Inactive",
  includeHistory: "Show History",
  sortByName: "Sort by Name",
  lastVerifiedDate: "Last Verified Date",
  verify: "Verify",
  addP2D: "Add P2D",
  close: "Close",
  errorTitle: "Failed to load Permission to Discuss",
  removePermissionToDiscuss:
    "Are you sure you want to remove this person from Permission to Discuss?",
  removeFromP2D: "Remove from P2D",
  cancel: "Cancel",
  removeP2DLabel: "Remove Permission to Discuss?",
  neverVerified: "Never Verified",
  removedDate: "Removal Effective Date",
  a0294Error:
    "A_0294 - WARNING: Remove Effective Date cannot be prior to the Active Effective Date.",
};

const MIN_EFFECTIVE_DATE = "1987-05-22";

type Props = {
  account: GetAccountResponse;
  className?: string;
  toolId: string;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onDisplayGeneralErrors: (err: unknown) => void;
  onRequestClose: () => void;
  referenceId?: string;
};

export default function PermissionToDiscuss({
  account,
  className,
  toolId,
  onSetToolInProgress,
  onDisplayGeneralErrors,
  onRequestClose,
  referenceId = "",
}: Props) {
  const { channel } = useChannelState();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeHistory, setIncludeHistory] = useState(false);
  const [sortByName, setSortByName] = useState(false);
  const [adding, setAdding] = useState(false);
  const [permissionToEditId, setPermissionToEditId] = useState("");
  const [removePermissionId, setRemovePermissionId] = useState("");
  const [removedDate, setRemovedDate] = useState<DateString>("");
  const [networkValidationMessages, setNetworkValidationMessages] =
    useState<ValidationErrors>();

  const req: GetPermissionToDiscussRequest = {
    partyId: account.partyId,
    includeHistory,
    includeInactive,
  };
  const customerInfoReq: GetCustomerInfoRequest = {
    dwbuid: account.currentDwbuid,
    referenceId,
    channel: ChannelMapToFull[channel],
  };

  const {
    data: getPermissionToDiscussResponse,
    isLoading: isGetPermissionToDiscussResponseLoading,
    isError: isGetPermissionToDiscussResponseError,
    error: getPermissionToDiscussResponseError,
    refetch: getPermissionToDiscussResponseRefetch,
  } = useGetPermissionToDiscuss(req);

  const { mutateAsync: addPermissionToDiscuss, isLoading: isAdding } =
    useAddPermissionToDiscuss(req, customerInfoReq);

  const { mutateAsync: editPermissionToDiscuss, isLoading: isEditing } =
    useEditPermissionToDiscuss(req, customerInfoReq);

  const {
    mutateAsync: verifyPermissionToDiscuss,
    isLoading: isVerificationInProgress,
  } = useVerifyPermissionToDiscuss(req, customerInfoReq);

  const { mutateAsync: removePermissionToDiscuss, isLoading: isRemoving } =
    useRemovePermissionToDicuss(req, customerInfoReq);

  useEffect(() => {
    if (adding || permissionToEditId) {
      onSetToolInProgress(toolId, true);
    } else {
      onSetToolInProgress(toolId, false);
    }
  }, [adding, permissionToEditId, onSetToolInProgress, toolId]);

  let permissionToRemoveModalName = "";
  if (removePermissionId && getPermissionToDiscussResponse) {
    const removeData =
      getPermissionToDiscussResponse.permissionsById[removePermissionId];
    permissionToRemoveModalName = `${removeData?.firstName} ${removeData?.lastName}`;
  }

  const sortedPermissionsList = useMemo((): PermissionToDiscussPermission[] => {
    if (!getPermissionToDiscussResponse?.permissionsList) {
      return [];
    }

    if (!sortByName) {
      return getPermissionToDiscussResponse.permissionsList;
    }

    const sorted = [...getPermissionToDiscussResponse.permissionsList];
    sorted.sort((a, b) => {
      const currLastName = a.lastName.toLowerCase();
      const nextLastName = b.lastName.toLowerCase();
      if (currLastName < nextLastName) {
        return -1;
      } else if (currLastName > nextLastName) {
        return 1;
      } else {
        const currFirstName = a.firstName.toLowerCase();
        const nextFirstName = b.firstName.toLowerCase();
        if (currFirstName < nextFirstName) {
          return -1;
        } else if (currFirstName > nextFirstName) {
          return 1;
        } else {
          return 0;
        }
      }
    });
    return sorted;
  }, [getPermissionToDiscussResponse?.permissionsList, sortByName]);

  const onAddPermissionToDiscuss = ({
    firstName,
    lastName,
    relationshipId,
    effectiveDate,
  }: AddPermisionToDiscuss): Promise<unknown> => {
    if (isAdding) {
      return new Promise<unknown>(() => null);
    }

    return addPermissionToDiscuss({
      dwbuid: account.currentDwbuid,
      effectiveDate,
      firstName,
      lastName,
      partyId: account.partyId,
      relationshipId,
      referenceId,
      channel: ChannelMapToFull[channel],
    }).then(() => setAdding(false));
  };

  const onEditPermission = (permissionToEdit: P2DEdit): Promise<unknown> => {
    if (isEditing || !permissionToEditId) {
      return new Promise<unknown>(() => null);
    }

    return editPermissionToDiscuss({
      partyId: account.partyId,
      permissionId: permissionToEdit.id,
      dwbuid: account.currentDwbuid,
      firstName: permissionToEdit.firstName,
      lastName: permissionToEdit.lastName,
      relationshipId: permissionToEdit.relationshipId,
      referenceId,
      channel: ChannelMapToFull[channel],
    })
      .then(() => setPermissionToEditId(""))
      .catch((err) => {
        onDisplayGeneralErrors(err);
        throw err;
      });
  };

  const verifyP2D = () => {
    if (isVerificationInProgress) {
      return;
    }

    verifyPermissionToDiscuss({
      partyId: account.partyId,
      dwbuid: account.currentDwbuid,
      referenceId,
      channel: ChannelMapToFull[channel],
    }).catch(onDisplayGeneralErrors);
  };

  const clearRemovedDateValidation = () => {
    if (networkValidationMessages) {
      setNetworkValidationMessages((prev) => ({
        ...prev,
        ["removedDate"]: [],
      }));
    }
  };

  const onCloseRemoveP2DModal = () => {
    setRemovePermissionId("");
    setRemovedDate("");
    clearRemovedDateValidation();
  };

  const onRemovePermissionToDiscuss = () => {
    if (isRemoving || !removePermissionId || !removedDate) {
      return;
    }

    const toRemove = sortedPermissionsList.find(
      (p) => p.id === removePermissionId,
    );
    if (!toRemove) {
      // This should not be possible
      return;
    }

    // If remove effective date is prior to effective date, show A_0294 error
    if (isBefore(parseISO(removedDate), parseISO(toRemove.effectiveDate))) {
      setNetworkValidationMessages((prev) => ({
        ...prev,
        ["removedDate"]: [strings.a0294Error],
      }));
      return;
    }

    clearRemovedDateValidation();

    removePermissionToDiscuss({
      partyId: account.partyId,
      permissionId: removePermissionId,
      removedDate,
      referenceId,
      channel: ChannelMapToFull[channel],
    })
      .then(() => {
        setRemovePermissionId("");
        setRemovedDate("");
      })
      .catch((err) =>
        setValidationErrorsAndDisplayGeneralErrors(
          err,
          setNetworkValidationMessages,
          () => null,
        ),
      );
  };

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col
            className={toolStyles.centerCol}
            md={
              isGetPermissionToDiscussResponseLoading ||
              isGetPermissionToDiscussResponseError
                ? 11
                : 2.25
            }
          >
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.PermissionToDiscuss)}
            </h2>
          </Col>
          {!isGetPermissionToDiscussResponseLoading &&
            !isGetPermissionToDiscussResponseError && (
              <>
                <Col md={4.75} className={toolStyles.centerCol}>
                  <StyledCheckbox
                    className={styles.checkbox}
                    label={strings.includeInactive}
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                  <StyledCheckbox
                    className={styles.checkbox}
                    label={strings.includeHistory}
                    checked={includeHistory}
                    onChange={(e) => setIncludeHistory(e.target.checked)}
                  />
                  <StyledCheckbox
                    className={styles.checkbox}
                    label={strings.sortByName}
                    checked={sortByName}
                    onChange={(e) => setSortByName(e.target.checked)}
                  />
                </Col>
                <Col md={1}>
                  <div className={styles.lastVerifiedLabel}>
                    {strings.lastVerifiedDate}
                  </div>
                  <div
                    className={classNames({
                      [styles.lastVerifiedValue]: true,
                      [styles.outdated]:
                        !getPermissionToDiscussResponse.lastVerifiedDate ||
                        isBefore(
                          new Date(
                            getPermissionToDiscussResponse.lastVerifiedDate,
                          ),
                          subMonths(new Date(), 6),
                        ),
                    })}
                  >
                    {getPermissionToDiscussResponse.lastVerifiedDate
                      ? convertDateStringToMonthDayYearFormat(
                          getPermissionToDiscussResponse.lastVerifiedDate,
                        )
                      : strings.neverVerified}
                  </div>
                </Col>
                <Col md={1} className={toolStyles.centerCol}>
                  <StyledButton
                    className={styles.button}
                    variant="secondary"
                    onClick={verifyP2D}
                    disabled={
                      isVerificationInProgress || !sortedPermissionsList.length
                    }
                  >
                    {strings.verify}
                  </StyledButton>
                </Col>
                <Col md={2} className={toolStyles.centerCol}>
                  <StyledButton
                    className={styles.addButton}
                    variant="secondary"
                    onClick={() => setAdding(true)}
                    disabled={adding || !!permissionToEditId}
                  >
                    {strings.addP2D}
                  </StyledButton>
                </Col>
              </>
            )}
          <Col md={1}>
            <StyledButton
              className={toolStyles.headerButton}
              variant="secondary"
              onClick={onRequestClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        {isGetPermissionToDiscussResponseLoading ? (
          <Row>
            <Col>
              <Spinner />
            </Col>
          </Row>
        ) : isGetPermissionToDiscussResponseError ? (
          <Row>
            <Col>
              <StyledError
                errorTitle={strings.errorTitle}
                error={getPermissionToDiscussResponseError}
                refetch={getPermissionToDiscussResponseRefetch}
              />
            </Col>
          </Row>
        ) : (
          <>
            {adding && !permissionToEditId ? (
              <AddPermissionToDiscuss
                onSubmit={onAddPermissionToDiscuss}
                onCancel={() => setAdding(false)}
                relationshipTypesById={
                  getPermissionToDiscussResponse.relationshipTypesById
                }
                isSubmitting={isAdding}
                isDisabled={isEditing}
                onDisplayGeneralErrors={onDisplayGeneralErrors}
              />
            ) : null}
            {permissionToEditId && !adding ? (
              <EditPermissionToDiscuss
                onSubmit={onEditPermission}
                onCancel={() => setPermissionToEditId("")}
                editableData={
                  getPermissionToDiscussResponse.permissionsById[
                    permissionToEditId
                  ]
                }
                relationshipTypesById={
                  getPermissionToDiscussResponse.relationshipTypesById
                }
                isSubmitting={isEditing}
                isDisabled={isAdding}
              />
            ) : null}
            <Row>
              <Col>
                <PermissionToDiscussTable
                  permissions={sortedPermissionsList}
                  onEdit={setPermissionToEditId}
                  onRemove={setRemovePermissionId}
                  disableEditing={adding || !!permissionToEditId}
                />
              </Col>
            </Row>
          </>
        )}
      </Container>
      <BaseModal
        isOpen={!!removePermissionId}
        label={strings.removeP2DLabel}
        onRequestClose={onCloseRemoveP2DModal}
        className={styles.modal}
      >
        <ModalCloseButton
          onClick={onCloseRemoveP2DModal}
          className={styles.modalCloseButton}
        />
        <img src={warningIcon} alt="" />
        <div className={styles.modalDesc}>
          {strings.removePermissionToDiscuss}
        </div>
        <h5>{permissionToRemoveModalName}</h5>
        <DateInput
          className={styles.removedDate}
          label={strings.removedDate}
          value={removedDate}
          onChangeDate={(d) => {
            clearRemovedDateValidation();
            setRemovedDate(d);
          }}
          showPopover={false}
          validationMessages={
            networkValidationMessages
              ? networkValidationMessages["removedDate"]
              : undefined
          }
          min={MIN_EFFECTIVE_DATE}
          max={format(new Date(), "yyyy-MM-dd").toString() as DateString}
        />
        <StyledButton
          variant="danger"
          className={styles.modalRemove}
          onClick={onRemovePermissionToDiscuss}
          disabled={!removedDate}
        >
          {strings.removeFromP2D}
        </StyledButton>
        <StyledButton
          variant="primary"
          className={styles.modalCancel}
          onClick={onCloseRemoveP2DModal}
        >
          {strings.cancel}
        </StyledButton>
      </BaseModal>
    </div>
  );
}
